<script setup>
import { ref, computed, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentsAPI from '../../../../api/agents'; // GK-HUB: set password API
// GK-HUB: Inbox/team assignment on agent edit
import InboxMembersAPI from '../../../../api/inboxMembers';
import TeamsAPI from '../../../../api/teams';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  // GK-HUB: availability prop removed — agent controls own status
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  // GK-HUB: Current inbox/team membership IDs
  inboxIds: {
    type: Array,
    default: () => [],
  },
  teamIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
// GK-HUB: availability removed from edit form — controlled by agent login/logout only
const selectedRoleId = ref(props.customRoleId || props.type);

// GK-HUB: Set password form state
const showPasswordForm = ref(false);
const newPassword = ref('');
const isSavingPassword = ref(false);

// GK-HUB: Inbox and team selection
const selectedInboxes = ref([]);
const selectedTeams = ref([]);
const isSavingAssignments = ref(false);

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');
// GK-HUB: Inbox/team lists from stores
const inboxesList = useMapGetter('inboxes/getActiveInboxes'); // GK-HUB: excludes deactivated;
const teamsList = useMapGetter('teams/getTeams');

// GK-HUB: Added supervisor and manager roles to agent edit form
const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'manager',
      name: 'manager',
      label: t('AGENT_MGMT.AGENT_TYPES.MANAGER'),
    },
    {
      id: 'supervisor',
      name: 'supervisor',
      label: t('AGENT_MGMT.AGENT_TYPES.SUPERVISOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});
// GK-HUB: end role list

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

// GK-HUB: availability select removed — agent controls own status via sidebar menu

// GK-HUB: Pre-populate inbox/team selections on mount
onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('teams/get');

  // Wait for store data and set initial selections based on props
  const setInitialSelections = () => {
    if (props.inboxIds?.length && inboxesList.value?.length) {
      selectedInboxes.value = inboxesList.value.filter(inbox =>
        props.inboxIds.includes(inbox.id)
      );
    }
    if (props.teamIds?.length && teamsList.value?.length) {
      selectedTeams.value = teamsList.value.filter(team =>
        props.teamIds.includes(team.id)
      );
    }
  };

  // Try immediately (stores may already be loaded)
  setInitialSelections();

  // Also watch for store updates in case data loads after mount
  const unwatch = store.watch(
    (state, getters) => [
      getters['inboxes/getActiveInboxes'], // GK-HUB: excludes deactivated
      getters['teams/getTeams'],
    ],
    () => {
      setInitialSelections();
      // Unwatch after first successful population
      if (selectedInboxes.value.length || selectedTeams.value.length) {
        unwatch();
      }
    }
  );
});
// GK-HUB: end pre-populate

// GK-HUB: Save inbox/team assignments
const saveAssignments = async () => {
  const promises = [];

  // Update inbox memberships
  const currentInboxIds = new Set(props.inboxIds || []);
  const newInboxIds = new Set(selectedInboxes.value.map(i => i.id));

  // For each inbox, we need to update its full member list
  // Add agent to new inboxes
  const inboxesToAdd = [...newInboxIds].filter(id => !currentInboxIds.has(id));
  inboxesToAdd.forEach(inboxId => {
    promises.push(InboxMembersAPI.addAgents({ inboxId, userIds: [props.id] }));
  });

  // Remove agent from removed inboxes — fetch current members first, then update without this agent
  const inboxesToRemove = [...currentInboxIds].filter(
    id => !newInboxIds.has(id)
  );
  await Promise.all(
    inboxesToRemove.map(async inboxId => {
      try {
        const response = await store.dispatch('inboxMembers/get', { inboxId });
        const currentMembers = response?.data?.payload || [];
        const updatedIds = currentMembers
          .map(m => m.id)
          .filter(id => id !== props.id);
        promises.push(
          InboxMembersAPI.update({ inboxId, agentList: updatedIds })
        );
      } catch {
        // If we can't fetch members, skip removal
      }
    })
  );

  // Update team memberships
  const currentTeamIds = new Set(props.teamIds || []);
  const newTeamIds = new Set(selectedTeams.value.map(team => team.id));

  const teamsToAdd = [...newTeamIds].filter(id => !currentTeamIds.has(id));
  teamsToAdd.forEach(teamId => {
    // GK-HUB (#115): dialeto members[{ user_id }] — sem flag, o backend aplica o default por role.
    // Antes usava agentsList (chave morta) → payload vazio → agente não era adicionado ao time (no-op).
    promises.push(
      TeamsAPI.addAgents({ teamId, members: [{ user_id: props.id }] })
    );
  });

  // Remove agent from removed teams — DELETE ALVO deste agente (não mais fetch-then-replace)
  const teamsToRemove = [...currentTeamIds].filter(id => !newTeamIds.has(id));
  teamsToRemove.forEach(teamId => {
    // GK-HUB (#115): remoção ALVO via DELETE user_ids:[props.id] — remove SÓ este agente, preserva os
    // demais e a flag deles, e funciona mesmo se ele for o ÚLTIMO membro (esvazia o time sem 422).
    // Substitui o fetch-then-replace anterior (getAgents + updateAgents com a lista restante), que:
    //  (a) mandava members:[] quando ele era o único membro → 422 do guard anti-wipe → falha engolida;
    //  (b) tinha race de lost-update (um add concorrente entre o getAgents e o PATCH era apagado).
    promises.push(TeamsAPI.removeAgents({ teamId, userIds: [props.id] }));
  });

  if (promises.length > 0) {
    await Promise.all(promises);
  }
};
// GK-HUB: end save assignments

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);

    // GK-HUB: Save inbox/team assignments
    isSavingAssignments.value = true;
    let assignmentsFailed = false;
    try {
      await saveAssignments();
    } catch {
      // GK-HUB (#115): fluxo crítico — NUNCA reportar sucesso quando a atribuição falhou.
      assignmentsFailed = true;
      useAlert(t('AGENT_MGMT.EDIT.ASSIGNMENTS_ERROR'));
    } finally {
      isSavingAssignments.value = false;
    }
    // GK-HUB: end save assignments

    // GK-HUB (#115): se a atribuição falhou, não emitir SUCCESS nem fechar (deixa o erro visível p/ retry)
    if (assignmentsFailed) return;
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

// GK-HUB: Set password directly (ISPs sem SMTP)
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{6,}$/;
const isPasswordValid = computed(() => passwordRegex.test(newPassword.value));

const setPassword = async () => {
  if (!isPasswordValid.value) return;
  isSavingPassword.value = true;
  try {
    await AgentsAPI.setPassword(props.id, newPassword.value);
    useAlert(t('AGENT_MGMT.EDIT.SET_PASSWORD.SUCCESS'));
    showPasswordForm.value = false;
    newPassword.value = '';
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.SET_PASSWORD.ERROR'));
  } finally {
    isSavingPassword.value = false;
  }
};

const cancelPasswordForm = () => {
  showPasswordForm.value = false;
  newPassword.value = '';
};
// GK-HUB: end set password
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <!-- GK-HUB: availability select removed — agent controls own status via sidebar menu -->

      <!-- GK-HUB: Inbox assignment on agent edit -->
      <div class="w-full">
        <label>
          {{ $t('AGENT_MGMT.EDIT.INBOXES.LABEL') }}
          <multiselect
            v-model="selectedInboxes"
            :options="inboxesList"
            track-by="id"
            label="name"
            multiple
            :close-on-select="false"
            :clear-on-select="false"
            hide-selected
            :placeholder="$t('AGENT_MGMT.EDIT.INBOXES.PLACEHOLDER')"
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
          />
          <p class="mt-0.5 mb-0 text-xs text-n-slate-10">
            {{ $t('AGENT_MGMT.EDIT.INBOXES.HELP') }}
          </p>
        </label>
      </div>

      <!-- GK-HUB: Team assignment on agent edit -->
      <div class="w-full">
        <label>
          {{ $t('AGENT_MGMT.EDIT.TEAMS.LABEL') }}
          <multiselect
            v-model="selectedTeams"
            :options="teamsList"
            track-by="id"
            label="name"
            multiple
            :close-on-select="false"
            :clear-on-select="false"
            hide-selected
            :placeholder="$t('AGENT_MGMT.EDIT.TEAMS.PLACEHOLDER')"
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
          />
          <p class="mt-0.5 mb-0 text-xs text-n-slate-10">
            {{ $t('AGENT_MGMT.EDIT.TEAMS.HELP') }}
          </p>
        </label>
      </div>
      <!-- GK-HUB: end inbox/team assignment -->

      <!-- GK-HUB: Set password inline form (ISPs sem SMTP) -->
      <div
        v-if="showPasswordForm && provider !== 'saml'"
        class="w-full p-3 mb-3 border rounded-lg border-n-strong bg-n-solid-2"
      >
        <label class="block mb-1 text-sm font-medium">
          {{ $t('AGENT_MGMT.EDIT.SET_PASSWORD.TITLE') }}
          <span class="text-xs font-normal text-n-slate-10">({{ email }})</span>
        </label>
        <div class="flex items-center gap-2">
          <input
            v-model="newPassword"
            type="password"
            class="!mb-0 flex-1"
            :placeholder="$t('AGENT_MGMT.EDIT.SET_PASSWORD.PLACEHOLDER')"
          />
          <Button
            type="button"
            :label="$t('AGENT_MGMT.EDIT.SET_PASSWORD.BUTTON')"
            :disabled="!isPasswordValid || isSavingPassword"
            :is-loading="isSavingPassword"
            @click.prevent="setPassword"
          />
          <Button
            faded
            slate
            type="button"
            :label="$t('AGENT_MGMT.EDIT.SET_PASSWORD.CANCEL')"
            @click.prevent="cancelPasswordForm"
          />
        </div>
        <p class="mt-1 mb-0 text-xs text-n-slate-10">
          {{ $t('AGENT_MGMT.EDIT.SET_PASSWORD.VALIDATION') }}
        </p>
      </div>
      <!-- GK-HUB: end set password form -->

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml' && !showPasswordForm"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="showPasswordForm = true"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating || isSavingAssignments"
            :is-loading="uiFlags.isUpdating || isSavingAssignments"
          />
        </div>
      </div>
    </form>
  </div>
</template>
